import React, { useState, useEffect, createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from './useUserRoles';

interface SignUpData {
  email: string;
  password: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  role?: UserRole;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (data: SignUpData) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signInWithApple: () => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (data: SignUpData) => {
    const { email, password, displayName, firstName, lastName, dateOfBirth, role = 'user' } = data;
    console.log('SignUp called with role:', role);
    const redirectUrl = `${window.location.origin}/`;
    
    const { data: authData, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName,
          first_name: firstName,
          last_name: lastName
        }
      }
    });

    console.log('SignUp result:', { user: !!authData.user, error: !!error });

    // Set user role and update profile if signup was successful
    if (!error && authData.user) {
      try {
        console.log('Setting user role to:', role, 'for user:', authData.user.id);
        // Insert or update user role
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .upsert({ 
            user_id: authData.user.id, 
            role: role 
          }, { 
            onConflict: 'user_id' 
          });
        
        if (roleError) {
          console.error('Role assignment error:', roleError);
        } else {
          console.log('User role set successfully:', role, roleData);
        }

        // Update profile with additional fields
        if (firstName || lastName || dateOfBirth) {
          const { error: profileError } = await supabase
            .from('profiles')
            .update({
              first_name: firstName,
              last_name: lastName,
              date_of_birth: dateOfBirth
            })
            .eq('user_id', authData.user.id);

          if (profileError) {
            console.error('Profile update error:', profileError);
          }
        }
      } catch (roleError) {
        console.error('Failed to set user role:', roleError);
      }

      // Send welcome email
      try {
        await supabase.functions.invoke('send-welcome-email', {
          body: { 
            email: authData.user.email,
            displayName: displayName 
          }
        });
        console.log('Welcome email sent successfully');
      } catch (emailError) {
        console.error('Failed to send welcome email:', emailError);
        // Don't fail the signup if email fails
      }
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      }
    });
    return { error };
  };

  const signInWithApple = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: `${window.location.origin}/`,
      }
    });
    return { error };
  };

  const signOut = async () => {
    try {
      // Clear local state first
      setUser(null);
      setSession(null);
      
      // Sign out globally (invalidates session on server too)
      const { error } = await supabase.auth.signOut();
      
      return { error };
    } catch (error) {
      console.error('SignOut error:', error);
      // Force clear even on error
      setUser(null);
      setSession(null);
      return { error };
    }
  };

  const value = {
    user,
    session,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signOut,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}